import { useAuthState, useClient } from '@based/sdk/react'
import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import { useCmsParams } from '../../hooks/useCmsParams.js'
import { useUsers } from '../../hooks/useUsers.js'
import { capitalize, normalize } from '../../utils/text.js'
import { AppHeader } from '../AppHeader/index.js'
import { Avatar } from '../Avatar/index.js'
import { FollowMe } from '../FollowMe/index.js'
import { Stack } from '../Stack/index.js'
import { Tabs } from '../Tabs/index.js'
import { Text } from '../Text/index.js'
import { UserMenu } from '../UserMenu/index.js'
import { Cms } from './index.js'
import { Tab } from './Tab.js'

export type ContainerProps = {
  children: ReactNode
  name: string
  followMe: boolean
  leadComponent?: ReactNode
  trailComponent?: ReactNode
  logo?: string
  logoutEndpoint?: string
  hideAccountSettings?: boolean
  hideWorkspaceSettings?: boolean
  hideDevelopmentControls?: boolean
}

export const Container: React.FC<ContainerProps> = ({
  children: childrenProp = [],
  name,
  followMe = true,
  leadComponent,
  trailComponent,
  logo,
  logoutEndpoint,
  hideAccountSettings,
  hideWorkspaceSettings,
  hideDevelopmentControls,
}) => {
  const children = Array.isArray(childrenProp) ? childrenProp : [childrenProp]
  const [params, go] = useCmsParams()
  const client = useClient()
  const { token } = useAuthState()
  const { me, all } = useUsers()
  const tabs: Tabs[] = []
  let activeTab: ReactElement | null = null
  let activeSection: string
  let authChild: ReactElement | null = null
  let onboardingChild: ReactElement | null = null

  const [redirect, setRedirect] = useState<ReactElement | null>(null)

  React.Children.forEach(children, (child) => {
    if (authChild && onboardingChild) return

    if (React.isValidElement(child) && child.type === Cms.Auth) {
      authChild = child
    }

    if (React.isValidElement(child) && child.type === Cms.Onboarding) {
      onboardingChild = child
    }
  })

  const needsAuthRedirect = !!authChild && !token
  const needsOnboarding =
    !!onboardingChild && !!token && me?.status === 'onboarding'

  useEffect(() => {
    if (params.section === 'auth' && params.t) {
      client.setAuthState({})
    }
  }, [params])

  useEffect(() => {
    if (needsAuthRedirect && !redirect) {
      const newParams: Record<string, any> = { page: 'auth' }
      if (params.t) {
        newParams.t = params.t
      }
      go(newParams, true)
      setRedirect(authChild)
    }
  }, [needsAuthRedirect, authChild, redirect, go])

  useEffect(() => {
    if (redirect && token) {
      setRedirect(null)
      go({ page: '' }, true)
    }
  }, [redirect, token])

  if (needsOnboarding) {
    return onboardingChild
  }

  if (redirect) {
    return <>{redirect}</>
  }

  if (needsAuthRedirect) {
    return null
  }

  const parseChildren = (children: Iterable<ReactNode>) => {
    for (const child of children) {
      if (typeof child === 'object' && 'type' in child) {
        if (child.type === Cms.Tab) {
          let { name, icon, children, section, hidden } = child.props

          const walk = (children: typeof childrenProp) => {
            if (!Array.isArray(children)) {
              children = [children]
            }
            for (const { props } of children) {
              if (props) {
                if (props.section) {
                  const normalized = normalize(props.section)
                  section ||= normalized
                  if (normalized === params.section) {
                    return true
                  }
                }
                if (props.children && walk(props.children)) {
                  return true
                }
              }
            }
          }

          const nestedActive = walk(children)

          if (
            nestedActive ||
            (params.section !== undefined &&
              section !== undefined &&
              params.section === section)
          ) {
            activeSection = section
            activeTab = child
          } else {
            activeSection ??= section
            activeTab ??= child
          }

          if (!hidden && name && section) {
            tabs.push({
              value: section?.toLowerCase(),
              label: capitalize(name),
              icon,
            })
          }
        }
      } else if (Array.isArray(child)) {
        parseChildren(child)
      }
    }
  }

  parseChildren(children)

  return (
    <>
      <AppHeader variant="main">
        <AppHeader.Left>
          {leadComponent || (
            <Stack
              fitContent
              justify="start"
              gap={12}
              style={{ padding: '6px 10px 6px 6px' }}
            >
              <Avatar label={name} src={logo} />
              <Text singleLine color="neutral80" variant="display-medium">
                {name}
              </Text>
            </Stack>
          )}
          {activeTab &&
            activeSection &&
            Array.isArray(tabs) &&
            tabs?.length !== 0 && (
              <Tabs
                value={activeSection}
                onChange={(section) => go({ section }, true)}
                tabs={tabs}
              />
            )}
        </AppHeader.Left>
        <AppHeader.Right>
          {followMe && Array.isArray(all) && all?.length !== 0 && (
            <FollowMe mode="full" />
          )}
          {trailComponent && trailComponent}
          <UserMenu
            logoutEndpoint={logoutEndpoint}
            hideAccountSettings={hideAccountSettings}
            hideWorkspaceSettings={hideWorkspaceSettings}
            hideDevelopmentControls={hideDevelopmentControls}
            followMe={followMe}
          />
        </AppHeader.Right>
      </AppHeader>
      {activeTab || <Tab name={name}>{children}</Tab>}
    </>
  )
}
